import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AppShell } from './components/layout/AppShell';
import { SignedInGuard } from './modules/auth';
import { PreviewPage } from './modules/preview/PreviewPage';
import { MyPreviewsPage } from './modules/preview/MyPreviewsPage';
import { PublicSurveyPage } from './modules/surveys/public/PublicSurveyPage';
import { HostedReportPage } from './modules/hostedReports';
import { PortalRoutes } from './modules/clientPortal/portal';
import { TesterInvitePage } from './modules/leadBot/public/TesterInvitePage';
import { AppRoutes } from './routes';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Ad Preview Mockups Stage 1 §4.1. Public, no-auth route —
              rendered OUTSIDE SignedInGuard + AppShell so franchisees
              never see the agency app chrome. The token in the URL is
              the only credential; the page fetches data from the
              corresponding public API endpoint. */}
          <Route path="/preview/:token" element={<PreviewPage />} />
          {/* Ad Preview Stage 3 multi-location amendment §A4. Public,
              no-auth contact-summary route -- a franchisee owning N
              locations opens this single URL (from one SMS) and sees
              one card per location. Also outside SignedInGuard +
              AppShell. */}
          <Route path="/my-previews/:token" element={<MyPreviewsPage />} />
          {/* Phase 6B Phase D. Public survey response page — no auth, token in
              the URL is the only credential. Outside SignedInGuard + AppShell. */}
          <Route path="/s/:token" element={<PublicSurveyPage />} />
          {/* Tools -> HTML Hosting. Public, no-auth viewer — outside
              SignedInGuard + AppShell so a client never sees the agency app.
              ⚠️ This page is OURS (state pages + the password gate); the
              uploaded report itself is embedded in an iframe pointed at the
              API's /content route, which serves it under a CSP sandbox in an
              opaque origin. Never render the HTML into this page. */}
          <Route path="/r/:token" element={<HostedReportPage />} />
          {/* Client Portal Phase 0. The CLIENT-facing route tree, rendered
              OUTSIDE SignedInGuard + AppShell -- those are the STAFF guard and
              the STAFF chrome. SignedInGuard enforces the kyck domain, so
              running a client through it would reject them, and AppShell is the
              agency sidebar a client must never see. The portal brings its own
              guard and its own (deliberately plain) shell; per-brand theming is
              Phase 2. Every byte of client data comes from /api/portal behind
              requirePortalAuth, which resolves scope server-side -- the guard
              below only decides which screen to draw. */}
          {/* SMS Lead Bot — the PUBLIC tester sign-up page. Outside
              SignedInGuard + AppShell: the person opening it has no kyck
              account and must never see the agency app.
              ⚠️ An INVITE LINK, not a public sign-up form — the token is an
              authorisation kyck issued, and that is what keeps the allowlist
              (the guard on autonomous sending) something we open deliberately
              rather than something the internet can. See
              src/types/botTesterInvite.ts. */}
          <Route path="/bot-test/:token" element={<TesterInvitePage />} />
          <Route path="/portal/*" element={<PortalRoutes />} />
          {/* Everything else: authed agency shell. */}
          <Route
            path="*"
            element={
              <SignedInGuard>
                <AppShell>
                  <AppRoutes />
                </AppShell>
              </SignedInGuard>
            }
          />
        </Routes>
      </BrowserRouter>
      {/* App-wide toast surface (Design System v1 §8 / Sub-phase G).
          Top-right, stacking downward; richColors tints success/error. */}
      <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
  );
}
